import {
  defineComponent,
  h,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { useNativeBridge } from "@app/vue/providers/NativeBridge";

type Props = {
  onScan: (data: string) => void;
  loading?: boolean;
  loadingLabel?: string;
};
/** Native camera in the POS WebView; local QR decoding for browser cameras. */
export const CameraPanel = defineComponent<Props>({
  props: ["onScan", "loading", "loadingLabel"],
  setup(props) {
    const { t } = useLocalization();
    const bridge = useNativeBridge();
    const frame = ref<HTMLDivElement | null>(null);
    const video = ref<HTMLVideoElement | null>(null);
    const state = ref<"starting" | "ready" | "unavailable">("starting");
    let stream: MediaStream | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let scanTimer: ReturnType<typeof setTimeout> | undefined;
    let generation = 0;
    let disposed = false;
    let nativeOpen = false;
    const stop = () => {
      generation++;
      clearTimeout(scanTimer);
      stream?.getTracks().forEach((track) => track.stop());
      stream = undefined;
      if (nativeOpen) bridge?.closeScanner();
      nativeOpen = false;
    };
    const found = (data: string) => {
      stop();
      props.onScan(data);
    };
    const start = async () => {
      stop();
      if (disposed || document.hidden || props.loading) return;
      const attempt = generation;
      state.value = "starting";
      await nextTick();
      try {
        if (bridge && frame.value) {
          const rect = frame.value.getBoundingClientRect();
          if (rect.top < 0 || rect.bottom > window.innerHeight) {
            state.value = "unavailable";
            return;
          }
          bridge.onScan((result) => {
            if (nativeOpen) found(result.data);
          });
          nativeOpen = true;
          await bridge.openScanner({
            dimensions: { width: rect.width, height: rect.height },
            position: { x: rect.left, y: rect.top },
          });
          if (disposed || attempt !== generation) {
            bridge.closeScanner();
            return;
          }
          state.value = "ready";
          return;
        }
        const media = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 960 },
          },
          audio: false,
        });
        if (disposed || attempt !== generation) {
          media.getTracks().forEach((track) => track.stop());
          return;
        }
        stream = media;
        const element = video.value!;
        element.srcObject = media;
        await element.play();
        const { default: jsQR } = await import("jsqr");
        if (disposed || attempt !== generation) return;
        state.value = "ready";
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d", { willReadFrequently: true })!;
        const decode = () => {
          if (disposed || attempt !== generation) return;
          if (element.readyState >= 2 && element.videoWidth > 0) {
            canvas.width = Math.min(640, element.videoWidth);
            canvas.height = Math.round(
              (canvas.width * element.videoHeight) / element.videoWidth,
            );
            context.drawImage(element, 0, 0, canvas.width, canvas.height);
            const pixels = context.getImageData(
              0,
              0,
              canvas.width,
              canvas.height,
            );
            const result = jsQR(pixels.data, canvas.width, canvas.height);
            if (result?.data) {
              found(result.data);
              return;
            }
          }
          scanTimer = setTimeout(decode, 180);
        };
        decode();
      } catch {
        if (!disposed && attempt === generation) {
          stop();
          state.value = "unavailable";
        }
      }
    };
    const reposition = () => {
      if (!bridge) return;
      stop();
      clearTimeout(timer);
      timer = setTimeout(start, 300);
    };
    const visibility = () => {
      if (document.hidden) stop();
      else start();
    };
    watch(
      () => props.loading,
      (loading) => {
        clearTimeout(timer);
        if (loading) stop();
        else timer = setTimeout(start, 400);
      },
    );
    onMounted(() => {
      // Let the modal/sheet entrance finish before positioning the native view.
      timer = setTimeout(start, 400);
      window.addEventListener("resize", reposition);
      window.addEventListener("scroll", reposition, true);
      document.addEventListener("visibilitychange", visibility);
    });
    onUnmounted(() => {
      disposed = true;
      stop();
      clearTimeout(timer);
      window.removeEventListener("resize", reposition);
      window.removeEventListener("scroll", reposition, true);
      document.removeEventListener("visibilitychange", visibility);
    });
    return () => (
      <section
        aria-busy={!!props.loading}
        class="overflow-hidden rounded-2xl border border-base-300 bg-base-200"
      >
        <div class="flex items-center justify-between gap-3 border-b border-base-300 px-4 py-3">
          <span class="flex items-center gap-2 text-sm font-semibold">
            <span class="material-symbols-outlined text-primary">
              qr_code_scanner
            </span>
            {t("loyalty.rewards.scan")}
          </span>
          <span
            class={`flex items-center gap-1.5 text-xs text-base-content/60 ${props.loading ? "invisible" : ""}`}
          >
            <span
              class={`h-1.5 w-1.5 rounded-full ${state.value === "ready" ? "bg-success" : "bg-base-content/30"}`}
            />
            {t(`loyalty.rewards.camera_${state.value}`)}
          </span>
        </div>
        <div
          ref={frame}
          class="relative flex aspect-square w-full shrink-0 items-center justify-center overflow-hidden bg-base-300"
        >
          {!bridge && (
            <video
              ref={video}
              muted
              autoplay
              playsinline
              class={`absolute inset-0 h-full w-full object-cover ${state.value === "ready" ? "opacity-100" : "opacity-0"}`}
            />
          )}
          <div
            aria-hidden="true"
            class="pointer-events-none absolute inset-8 rounded-2xl border border-primary/20 sm:inset-12"
          >
            {[
              "left-0 top-0 border-l-2 border-t-2 rounded-tl-2xl",
              "right-0 top-0 border-r-2 border-t-2 rounded-tr-2xl",
              "left-0 bottom-0 border-l-2 border-b-2 rounded-bl-2xl",
              "right-0 bottom-0 border-r-2 border-b-2 rounded-br-2xl",
            ].map((c) => (
              <span class={`absolute h-8 w-8 border-primary ${c}`} />
            ))}
          </div>
          {!props.loading && state.value !== "ready" && (
            <div class="relative z-10 max-w-64 space-y-3 px-5 text-center">
              <span class="material-symbols-outlined text-4xl text-base-content/50">
                {state.value === "starting" ? "photo_camera" : "videocam_off"}
              </span>
              <p class="text-sm text-base-content/70">
                {t(
                  state.value === "starting"
                    ? "loyalty.rewards.camera_hint"
                    : "loyalty.rewards.camera_help",
                )}
              </p>
              {state.value === "unavailable" && (
                <button
                  type="button"
                  class="btn btn-primary btn-sm min-h-10"
                  onClick={start}
                >
                  {t("loyalty.rewards.camera_retry")}
                </button>
              )}
            </div>
          )}
          {props.loading && (
            <div
              role="status"
              class="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-base-300/90 px-5 text-center"
            >
              <span
                class="loading loading-spinner text-primary"
                aria-hidden="true"
              />
              <p class="text-sm font-medium text-base-content">
                {props.loadingLabel}
              </p>
            </div>
          )}
        </div>
        <p class="px-4 py-3 text-center text-xs text-base-content/60">
          {t("loyalty.rewards.scan_hint")}
        </p>
      </section>
    );
  },
});
